import {
  postDeviceTelemetryBatch,
  type BufferedTelemetryEntry,
} from "@workspace/api-client-react";
import {
  buildTelemetryChunks,
  flushTelemetryChunks,
  serializedEntryBytes,
} from "@workspace/player-shell";
import { isOfflineCapablePlayer } from "@/lib/platform";
import {
  deleteEvent,
  peekEvents,
  type QueuedEvent,
} from "@/player/lib/offline-store";
import { playerDebugLog } from "@/player/lib/player-debug";

function toBatchEntry(event: QueuedEvent): BufferedTelemetryEntry {
  const clientId =
    event.clientEventId ?? (event.id != null ? String(event.id) : undefined);
  return {
    kind: event.kind ?? "now-playing",
    at: new Date(event.at).toISOString(),
    payload: event.payload as BufferedTelemetryEntry["payload"],
    ...(clientId ? { id: clientId } : {}),
  };
}

/**
 * Flush the offline telemetry queue to the batch ingestion endpoint.
 *
 * The queue is sent in chunks bounded by count AND serialized bytes (policy:
 * @workspace/player-shell telemetry-chunking) so a large inherited queue can
 * never outgrow the server's JSON body limit and 413 forever; each delivered
 * chunk's events are deleted immediately, so progress is durable. A 413
 * halves the chunk down to the poison entry, which is dropped; other 4xx
 * (except 408/429) stop the cycle WITHOUT touching the queue; network errors,
 * 5xx, 408 and 429 retry with exponential backoff.
 */
export async function flushTelemetryQueue(): Promise<boolean> {
  if (!isOfflineCapablePlayer()) return true;

  const events = await peekEvents();
  if (events.length === 0) return true;

  const chunks = buildTelemetryChunks(events, {
    measure: (event) => serializedEntryBytes(toBatchEntry(event)),
  });

  return flushTelemetryChunks(chunks, {
    send: (chunk) =>
      postDeviceTelemetryBatch({ entries: chunk.map(toBatchEntry) }),
    // Delete only the events we actually sent, by id — never clear-all.
    // Events enqueued while a chunk's POST was in flight must survive to
    // the next flush (peek -> send -> delete, per docs/player-offline.md).
    commit: async (chunk) => {
      for (const event of chunk) {
        if (event.id != null) await deleteEvent(event.id);
      }
    },
    onPoison: (event) => {
      // The server rejects this entry even alone; keeping it would wedge the
      // queue forever, so it is deleted with the chunk.
      console.warn("[player] dropping oversized telemetry entry", {
        kind: event.kind,
        id: event.id ?? null,
      });
      playerDebugLog("telemetry.poison-dropped", {
        kind: event.kind,
        id: event.id ?? null,
        at: event.at,
      });
    },
  });
}
